import React, { useCallback, useMemo } from "react";
import {
  Box,
  IconButton,
  TableCell,
  Badge,
  Tooltip,
  Chip,
  alpha,
} from "@mui/material";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import BusinessIcon from "@mui/icons-material/Business";
import CopyButton from "bundles/Internal/components/common/CopyButton";
import CompanyTableFilters, {
  TEAM_SIZE_LABELS,
} from "bundles/Internal/components/company/CompanyTableFilters";
import DataTable from "bundles/Internal/components/common/DataTable";
import FollowButton from "bundles/Internal/components/common/FollowButton";

export const COLUMNS_CONSTANTS = [
  { key: "id", label: "Id" },
  { key: "name", label: "Name" },
  { key: "country", label: "Country" },
  { key: "company_industry", label: "Industry" },
  { key: "city", label: "City" },
  { key: "zipcode", label: "Zipcode" },
  { key: "team_size", label: "Team size" },
  { key: "region", label: "Region" },
  { key: "sub_sector", label: "Sub sector" },
  { key: "recruitment_needs", label: "Recruitment needs" },
  { key: "recruitment_plan", label: "Recruitment plan" },
  { key: "view_count", label: "View count" },
  { key: "creation_year", label: "Creation year" },
  { key: "created_at", label: "Created at" },
  { key: "deleted_at", label: "Deleted at" },
];

export const INDUSTRY_COLOR = {
  architecture:   { border: "#1976D2", bg: "rgba(25,118,210,0.10)",   text: "#1976D2" },
  engineering:    { border: "#079344", bg: "rgba(7,147,68,0.10)",     text: "#079344" },
  construction:   { border: "#F57C00", bg: "rgba(245,124,0,0.10)",    text: "#F57C00" },
  administration: { border: "#6A1B9A", bg: "rgba(106,27,154,0.10)",   text: "#6A1B9A" },
  industry:       { border: "#757575", bg: "rgba(117,117,117,0.10)",  text: "#757575" },
};

const pill = (label, c) => (
  <Chip
    label={String(label).replaceAll("_", " ")}
    size="small"
    sx={{
      backgroundColor: c.bg,
      color: c.text,
      fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
      fontSize: 11,
      fontWeight: 600,
      borderRadius: "8px",
      height: 24,
    }}
  />
);

const formatValue = (value) => {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat("fr-BE", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(date);
};

const DATE_KEYS = new Set(["created_at", "deleted_at"]);

function formatCompanyCell(company, key) {
  const value = company?.[key];

  if (DATE_KEYS.has(key)) return formatDateTime(value);

  if (key === "team_size") {
    if (!value) return "";
    return TEAM_SIZE_LABELS[value] || value;
  }

  if (key === "company_industry" && value) {
    return pill(value, INDUSTRY_COLOR[value] || INDUSTRY_COLOR.industry);
  }

  if (key === "domain_name") {
    if (!value) return "";
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, minWidth: 0, maxWidth: 220 }}>
        <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0, flex: 1 }}>
          {value}
        </Box>
        <CopyButton value={value} sx={{ flexShrink: 0 }} />
      </Box>
    );
  }

  if (key === "social_links" || key === "feature_flags") {
    if (!value || (typeof value === "object" && Object.keys(value).length === 0)) return "";
    return JSON.stringify(value);
  }

  if (key === "recruitment_process" || key === "awards_data") {
    if (!Array.isArray(value) || value.length === 0) return "";
    return `${value.length} entr${value.length === 1 ? "y" : "ies"}`;
  }

  return formatValue(value);
}

const CompanyTable = ({
  companies = [],
  onCompanySelect,
  onOpenNotes,
  onQueryChange,
  filters,
  onFiltersChange,
  page,
  totalCount,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  loading = false,
  selectable = false,
  selectedRows = [],
  onRowSelect,
  onExportCsv,
  followedCompanyIds,
  onToggleFollow,
}) => {
  const columns = useMemo(() => COLUMNS_CONSTANTS, []);

  const renderCell = useCallback((company, key) => formatCompanyCell(company, key), []);

  return (
    <DataTable
      title="Companies"
      totalLabel={(count) => `${count.toLocaleString()} total companies`}
      emptyIcon={BusinessIcon}
      emptyTitle="No companies found"
      emptySubtitle="Try adjusting your search criteria"
      columns={columns}
      data={companies}
      renderCell={renderCell}
      extraHeadColumns={["Notes"]}
      renderExtraCells={(company) => {
        const notesCount = Number(company?.notes_count || 0);
        return (
          <TableCell key="notes" sx={{ whiteSpace: "nowrap", py: 1.5 }}>
            <Tooltip title="View notes" arrow placement="top">
              <IconButton
                size="small"
                aria-label="Open notes"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenNotes?.(company);
                }}
                disabled={!onOpenNotes}
                sx={{
                  backgroundColor:
                    notesCount > 0
                      ? alpha("#079344", 0.1)
                      : "transparent",
                  "&:hover": {
                    backgroundColor: alpha("#079344", 0.2),
                  },
                }}
              >
                <Badge
                  badgeContent={notesCount}
                  color="primary"
                  invisible={notesCount === 0}
                  overlap="circular"
                  sx={{
                    "& .MuiBadge-badge": {
                      backgroundColor: "#079344",
                      fontWeight: 600,
                      fontSize: 10,
                    },
                  }}
                >
                  <NoteAddOutlinedIcon
                    fontSize="small"
                    sx={{
                      color:
                        notesCount > 0 ? "#079344" : "text.secondary",
                    }}
                  />
                </Badge>
              </IconButton>
            </Tooltip>
          </TableCell>
        );
      }}
      tailHeadColumns={["Follow"]}
      renderTailCells={(company) => (
        <TableCell key="follow" sx={{ whiteSpace: "nowrap", py: 1.5, width: 48 }} onClick={(e) => e.stopPropagation()}>
          <FollowButton
            followableType="Company"
            followableId={company.id}
            followed={followedCompanyIds ? followedCompanyIds.has(company.id) : Boolean(company.followed)}
            onChange={(next) => onToggleFollow?.(company.id, next)}
          />
        </TableCell>
      )}
      onRowClick={onCompanySelect}
      onQueryChange={onQueryChange}
      searchPlaceholder="Search by name, country, industry, city…"
      searchInfo="Name, industry, city, country"
      searchById
      filtersComponent={
        filters && onFiltersChange ? (
          <CompanyTableFilters filters={filters} onFiltersChange={onFiltersChange} />
        ) : null
      }
      page={page}
      totalCount={totalCount}
      rowsPerPage={rowsPerPage}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
      loading={loading}
      selectable={selectable}
      selectedRows={selectedRows}
      onRowSelect={onRowSelect}
      onExportCsv={onExportCsv}
    />
  );
};

export default CompanyTable;
